/**
 * RepairsBuilderTab — Iter159 Phase B.2.
 *
 * The Repairs tab is now a focused **Stage-1 mirror** of the Restoration
 * builder. It renders the SAME per-subtype repair sub-builders
 * (RestorationConcreteRepairs · RestorationIronRepairs ·
 * RestorationTinTileRepairs) PLUS the Stage-1 add-ons (rescrew · pointing
 * · panel-beat) — but NOT Stage-2 paint, NOT the Combiner discount, NOT
 * the snapshot lock. Operators who only want a repairs quote get a
 * clean focused UI; the Restoration tab continues to bundle Repairs +
 * Paint for the full package.
 *
 * Because both tabs read/write the SAME MongoDB collections
 * (restoration_concrete_scenarios, restoration_iron_repairs,
 * restoration_tin_tile_repairs, plus restoration_* fields on the
 * appraisal), edits made here are automatically visible on the
 * Restoration tab and vice-versa.
 *
 * Push behaviour: per user choice 4b, Repairs push uses
 * import_source='repairs_builder' so it does NOT overwrite a Restoration
 * push (import_source='restoration_builder'). The two coexist on the
 * Components tab.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { QuoteTotalStrip } from '@/components/QuoteTotalStrip';
import SaveDocButton from './SaveDocButton';
import { Loader2, RefreshCw, Printer, Wrench, AlertTriangle, CheckCircle2, Upload, Layers, FileText } from 'lucide-react';
import { CalcShadowProvider, CalcShadowToggle, CalcShadow } from '@/components/CalcShadow';
import RestorationConcreteRepairs from '@/components/RestorationConcreteRepairs';
import RestorationIronRepairs, { isIronSubtype } from '@/components/RestorationIronRepairs';
import RestorationTinTileRepairs, { isTinTileSubtype } from '@/components/RestorationTinTileRepairs';
import CompareQuotesDialog from '@/components/CompareQuotesDialog';
import { RestorationDimsCard, subtypeBuilderLabel } from '@/components/RestorationBuilderTab';
import BuilderRoofDetailsPanel from '@/components/BuilderRoofDetailsPanel';
import { ConfidenceBanner } from '@/components/ConfidenceBanner';
import { RulesTraceDrawer } from '@/components/RulesTraceDrawer';
import { resolveEffectiveRoofSubtype } from '@/lib/roofSubtype';

/** Sprint S4 (2026-02c) · auto-fill chip. Reads a `stage1-preview`
 *  `auto_qty_options[rule_id]` entry and renders "auto: 44.2 lm ·
 *  Confirmed [Apply]". Clicking Apply pushes the qty into the input's
 *  form field via `onApply(qty)`. Never fires when the rule doesn't
 *  match or when qty=0. */
function AutoFillChip({ option, onApply, currentValue, testid }) {
  if (!option || !option.qty || option.qty <= 0) return null;
  const isApplied = Number(currentValue) === Number(option.qty);
  return (
    <div className="mt-1 flex items-center gap-1.5 flex-wrap" data-testid={testid}>
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Auto-fill</span>
      <span className="font-mono-num text-xs font-semibold text-slate-700">{option.qty.toFixed(2)} {option.unit}</span>
      <ConfidenceBanner
        source={option.source}
        confidence={option.confidence}
        label={option.rule_id}
        compact
      />
      <button
        type="button"
        data-testid={`${testid}-apply-btn`}
        onClick={() => onApply(option.qty)}
        disabled={isApplied}
        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${isApplied ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'text-[#D35400] border-orange-200 bg-orange-50 hover:bg-orange-100'}`}
        title={isApplied ? 'Already applied · edit the field to override' : `Apply this qty · rule ${option.rule_id} · ${option.op_ref}`}
      >
        {isApplied ? 'Applied' : 'Apply'}
      </button>
      {option.breakdown && option.breakdown.length > 1 && (
        <span className="text-[10px] text-slate-400" title={option.breakdown.map(p => `${p.label}: ${p.value}`).join(' + ')}>
          ({option.breakdown.map(p => (p.value || 0).toFixed(1)).join(' + ')})
        </span>
      )}
    </div>
  );
}

export default function RepairsBuilderTab({ api, appraisalId, appraisal, onRefresh, onJumpToPin, onOpenAnnotator }) {
  const [bundle, setBundle] = useState(null);
  const [dims, setDims] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  const [showRulesTrace, setShowRulesTrace] = useState(false);
  // Stage-1 add-on fields (rescrew $/m², pointing $/lm, panel-beat $/each)
  // live on the appraisal — same fields the Restoration builder writes
  // so any edit here propagates over.
  const [form, setForm] = useState({
    restoration_rescrew_m2:      0,
    restoration_pointing_lm:     0,
    restoration_panel_beat_qty:  0,
    // Also tracked so RestorationDimsCard's Paint forecast row renders
    // a "—" instead of throwing on undefined paint info. The Repairs
    // tab doesn't expose paint controls but the dims card is shared.
    restoration_paint_system_id: '',
    restoration_roof_m2:         0,
  });

  const subtype = resolveEffectiveRoofSubtype(appraisal, bundle?.meta);

  const load = useCallback(async (silent = false) => {
    if (silent !== true) setLoading(true);
    try {
      const [preview, dimsRes] = await Promise.all([
        api.get(`/repairs/appraisals/${appraisalId}/stage1-preview`),
        api.get(`/appraisals/${appraisalId}/reroof-builder/dims`).catch(() => ({ data: null })),
      ]);
      setBundle(preview.data);
      setDims(dimsRes.data || null);
      // Iter211 — pre-fill the rescrew quantity from the roof m² when
      // the appraisal's quote type explicitly names rescrew (e.g.
      // "Rescrew and Repair Longrun"). Only fires when the operator
      // hasn't already set a value, so subsequent loads don't clobber
      // their manual edit. They still need to hit "Save add-ons" to
      // persist — gives a clear undo path if the auto-fill is wrong.
      const qtRef = (appraisal?.quotation_type || appraisal?.quote_type_ref || '').toLowerCase();
      const wantsRescrew = qtRef.includes('rescrew');
      const roofM2 = Number(dimsRes.data?.roof_m2)
        || Number(appraisal?.restoration_roof_m2)
        || 0;
      const savedRescrew = Number(appraisal?.restoration_rescrew_m2) || 0;
      const autoRescrew = (wantsRescrew && savedRescrew === 0 && roofM2 > 0)
        ? roofM2
        : savedRescrew;
      setForm({
        restoration_rescrew_m2:      autoRescrew,
        restoration_pointing_lm:     appraisal?.restoration_pointing_lm || 0,
        restoration_panel_beat_qty:  appraisal?.restoration_panel_beat_qty || 0,
        restoration_paint_system_id: appraisal?.restoration_paint_system_id || '',
        restoration_roof_m2:         appraisal?.restoration_roof_m2 || 0,
        // Track whether the rescrew value is the auto-fill so the UI
        // can surface a "From your quote type" hint until the operator
        // saves (or edits) it.
        _rescrew_autofilled: (wantsRescrew && savedRescrew === 0 && roofM2 > 0),
      });
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  // iter313c H4 fix — depend on the specific `appraisal.*` fields the
  // form seeds from. Previously deps were `[api, appraisalId, subtype]`
  // with `appraisal` intentionally omitted, so a memoized stale
  // closure re-seeded `form` from the pre-save `appraisal` prop after
  // every save → operator's edit visibly reverted. Now load re-runs
  // (and reseeds correctly) when the parent passes down fresh values.
  }, [api, appraisalId, subtype, appraisal?.restoration_rescrew_m2, appraisal?.restoration_pointing_lm, appraisal?.restoration_panel_beat_qty, appraisal?.restoration_paint_system_id, appraisal?.restoration_roof_m2, appraisal?.quote_type_ref]);

  useEffect(() => { load(); }, [load]);
  // Flashing / Dektite edits (embed) change this quote's total — refresh the summary.
  useEffect(() => {
    const h = () => load(true);
    window.addEventListener('rtr:child-quote-refresh', h);
    return () => window.removeEventListener('rtr:child-quote-refresh', h);
  }, [load]);

  const saveForm = async () => {
    setSaving(true);
    try {
      // Wave 2 H9 fix (2026-02) — TRUE partial PUT: send ONLY the
      // Stage-1 add-on fields this builder owns. Backend's
      // `exclude_unset=True` preserves every other field. Previous
      // `...appraisal` spread caused subtype-revert races.
      await api.put(`/appraisals/${appraisalId}`, {
        restoration_rescrew_m2:     Number(form.restoration_rescrew_m2) || 0,
        restoration_pointing_lm:    Number(form.restoration_pointing_lm) || 0,
        restoration_panel_beat_qty: parseInt(form.restoration_panel_beat_qty, 10) || 0,
      });
      // iter313c H4 fix — DON'T `await load()` here. The load()
      // useCallback closes over the pre-save `appraisal` prop and
      // would re-seed `form` with the OLD values, making the operator's
      // edit visibly revert. Instead call onRefresh() to bubble up;
      // parent passes fresh `appraisal` down; deps change → load
      // re-fires via useEffect with the correct values.
      if (onRefresh) await onRefresh();
      setFlash({ tone: 'ok', msg: 'Repairs config saved.' });
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const onPush = async () => {
    if (!window.confirm("Push these repair lines onto the Components tab? Existing 'repairs_builder' lines on this appraisal will be replaced. (Won't touch Restoration-builder lines.)")) return;
    setPushing(true);
    setFlash(null);
    try {
      const { data } = await api.post(`/repairs/appraisals/${appraisalId}/push-stage1`);
      window.dispatchEvent(new Event('rtr:components-refresh'));
      setFlash({ tone: 'ok', msg: `Pushed ${data.inserted} repair line${data.inserted === 1 ? '' : 's'} (Stage-1 total $${data.summary.stage1_total.toFixed(2)})` });
      if (onRefresh) onRefresh();
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    } finally {
      setPushing(false);
    }
  };

  const err = bundle?.error;
  const addons = bundle?.meta?.available_addons || {};
  const s = bundle?.summary || {};
  const showRescrew   = addons.rescrew_per_m2 != null;
  const showPointing  = addons.pointing_per_lm != null;
  const showPanelBeat = addons.panel_beat_per_each != null;

  return (
    <CalcShadowProvider defaultShow={true}>
    <div className="space-y-4" data-testid="repairs-builder-tab">
      <div className="flex justify-end">
        <SaveDocButton
          appraisalId={appraisalId}
          docKind="quotation_detailed"
          label="Save Quote PDF"
          expandChildren={false}
          variant="outline"
          size="sm"
          testId="repairs-save-quote-pdf"
        />
      </div>
      {/* Header — same layout as Restoration but tinted orange to
          distinguish at a glance. */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-orange-200 rounded-md p-4 flex items-start justify-between gap-3">
        <div className="flex-1">
          <h3 className="text-lg font-bold text-orange-900 flex items-center gap-2 font-[Manrope]">
            <Wrench size={18} /> {(subtype ? subtypeBuilderLabel(subtype).replace('Restoration', 'Repairs') : 'Repairs')} Quote Builder
          </h3>
          <p className="text-xs text-orange-800/80 mt-1 max-w-2xl">
            Stage-1 repairs only — per-subtype scenarios for{' '}
            {/* Iter211 — explicit "Linked from Roof Details · {subtype}"
                chip so the operator can see WHERE this value comes from
                AND jump to the Roof Details panel to edit it. Replaces
                the confusion of seeing "Roof Subtype" referenced but no
                obvious field to set. */}
            {subtype ? (
              <button
                type="button"
                data-testid="repairs-roof-subtype-chip"
                onClick={() => {
                  const panel = document.querySelector('[data-testid="builder-roof-details-panel"]');
                  if (panel) {
                    panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    panel.classList.add('ring-2', 'ring-orange-400');
                    setTimeout(() => panel.classList.remove('ring-2', 'ring-orange-400'), 1800);
                  }
                }}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded ring-1 ring-orange-300 bg-white/70 hover:bg-orange-100 text-orange-900 text-[11px] font-bold align-middle"
                title="Linked from Roof Details — click to jump down and edit the Roof Type"
              >
                🔗 {subtype}
              </button>
            ) : <strong>—</strong>}{' '}
            roofs + Stage-1 add-ons (rescrew · pointing · panel-beat).
            <strong className="text-orange-900"> Data is shared with the Restoration tab</strong> — edit either, both stay in sync. For a Repairs + Paint package, use the Restoration tab&apos;s Combiner.
          </p>
        </div>
        <button onClick={load} data-testid="repairs-refresh-btn" className="text-sm border border-orange-300 bg-white text-orange-800 rounded-md px-3 py-1.5 hover:bg-orange-100 flex items-center gap-1 self-start">
          <RefreshCw size={13} /> Refresh
        </button>
        <button onClick={() => setShowCompare(true)} data-testid="repairs-compare-btn"
                className="text-sm border border-emerald-300 bg-emerald-50 text-emerald-800 rounded-md px-3 py-1.5 hover:bg-emerald-100 flex items-center gap-1 self-start">
          <FileText size={13} /> Compare Quotes
        </button>
        <button onClick={() => setShowRulesTrace(true)} data-testid="repairs-rules-trace-btn"
                title="Why did this auto-qty fire (or not)? Replay the rules engine against this appraisal."
                className="text-sm border border-orange-300 bg-white text-orange-800 rounded-md px-3 py-1.5 hover:bg-orange-50 flex items-center gap-1 self-start">
          <Wrench size={13} /> Rules Trace
        </button>
      </div>

      <RulesTraceDrawer
        api={api}
        appraisalId={appraisalId}
        open={showRulesTrace}
        onClose={() => setShowRulesTrace(false)}
      />

      {/* Iter167 — Roof Details quick-edit panel under the Repairs banner. */}
      <BuilderRoofDetailsPanel api={api} appraisalId={appraisalId} onApplied={load} />

      <CompareQuotesDialog api={api} appraisalId={appraisalId}
                            open={showCompare} onOpenChange={setShowCompare} />

      {flash && (
        <div data-testid="repairs-flash" className={`text-sm rounded-md p-3 border ${flash.tone === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
          {flash.tone === 'ok' ? <CheckCircle2 size={14} className="inline mr-1" /> : <AlertTriangle size={14} className="inline mr-1" />}
          {flash.msg}
        </div>
      )}

      {loading && !bundle ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 p-6">
          <Loader2 size={14} className="animate-spin" /> Loading Stage-1 repair preview…
        </div>
      ) : (
        <>
          {/* Shared live-dimensions card — same component as Restoration */}
          {dims && <RestorationDimsCard dims={dims} bundle={bundle} form={form} onRefresh={load} />}

          {err ? (
            <div data-testid="repairs-error" className="bg-rose-50 border border-rose-200 rounded-md p-4 text-sm text-rose-800">
              <div className="font-semibold flex items-center gap-2"><AlertTriangle size={14} /> Cannot build repair quote</div>
              <div className="mt-1">{bundle.message}</div>
            </div>
          ) : (
            <>
              {/* Per-subtype repair builder sub-components — these
                  components read/write to the same DB collections the
                  Restoration tab uses, so edits propagate both ways
                  automatically.
                  Iter211 — Concrete-scenarios block is irrelevant on
                  Iron-style roofs; hidden when the subtype is in the
                  Iron family so the screen isn't cluttered with a card
                  that has nothing to configure. */}
              {!isIronSubtype(subtype) && !isTinTileSubtype(subtype) && (
                <RestorationConcreteRepairs
                  allowSeparateQuote
                  api={api}
                  appraisalId={appraisalId}
                  subtype={subtype}
                  onLinesChanged={load}
                  onJumpToPin={onJumpToPin}
                  onOpenAnnotator={onOpenAnnotator}
                  paintHiddenByDefault={true}
                />
              )}

              {isIronSubtype(subtype) && (
                <RestorationIronRepairs
                  api={api}
                  appraisalId={appraisalId}
                  subtype={subtype}
                  onLinesChanged={load}
                  onJumpToPin={onJumpToPin}
                  onOpenAnnotator={onOpenAnnotator}
                  paintHiddenByDefault={true}
                />
              )}

              {isTinTileSubtype(subtype) && (
                <RestorationTinTileRepairs
                  api={api}
                  appraisalId={appraisalId}
                  subtype={subtype}
                  onLinesChanged={load}
                  onJumpToPin={onJumpToPin}
                  onOpenAnnotator={onOpenAnnotator}
                  paintHiddenByDefault={true}
                />
              )}

              {/* v10.19 (Feb 2026 · UAT A999588) · Yellow "Stage 1
                  add-ons" card removed. Backend was double-counting
                  the same ridge/valley/rescrew work in both the
                  scenario builder AND the legacy defect-catalogue
                  engine, inflating the quote by whatever the second
                  engine happened to emit. Now `stage1_addons_total`
                  is guaranteed $0 from the backend, so Repairs total
                  == Stage 1 repairs. One card, one number, no drift. */}
              <div className="grid grid-cols-3 gap-3" data-testid="repairs-summary">
                <SummaryCard label="Stage 1 repairs" value={`$${(s.stage1_repair_total || 0).toFixed(2)}`} tone="sky" />
                <SummaryCard label="Flashings & Dektites" value={`$${(s.flashing_total || 0).toFixed(2)}`} tone="sky" testid="repairs-flashing-total" />
                <SummaryCard label="Repairs total" value={`$${(s.quote_lines_total ?? s.stage1_total ?? 0).toFixed(2)}`} tone="orange" big testid="repairs-total" />
              </div>
              <QuoteTotalStrip api={api} appraisalId={appraisalId} linesTotal={s.quote_lines_total ?? s.stage1_total ?? 0} testid="repairs-quote-total-strip" />
              <CalcShadow testid="repairs-grand-total-calc" tone="orange">
                Σ repair lines <strong>${(s.stage1_total || 0).toFixed(2)}</strong> + flashings &amp; Dektites <strong>${(s.flashing_total || 0).toFixed(2)}</strong> = <strong>${(s.quote_lines_total ?? s.stage1_total ?? 0).toFixed(2)}</strong> · the quote total
              </CalcShadow>

              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="repairs-push-btn"
                  onClick={onPush}
                  disabled={pushing || (s.line_count === 0)}
                  className="bg-orange-600 text-white text-sm font-semibold rounded-md px-4 py-2 hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {pushing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {pushing ? 'Pushing…' : 'Push Repairs to Components'}
                </button>
                <button
                  data-testid="repairs-pdf-btn"
                  onClick={() => window.open(`${api.defaults.baseURL}/repairs/appraisals/${appraisalId}/quote.pdf`, '_blank')}
                  className="bg-white border border-slate-300 text-slate-700 text-sm font-semibold rounded-md px-4 py-2 hover:bg-slate-50 flex items-center gap-2"
                >
                  <Printer size={14} /> Repair Quote PDF
                </button>
                <div className="ml-auto">
                  <CalcShadowToggle testid="repairs-calcs-toggle" />
                </div>
              </div>

              {/* Stage-1 add-on quantities — same fields the Restoration
                  tab's Advanced panel exposes. Collapsed by default;
                  shows only the add-ons that apply to the current
                  subtype (Iron → rescrew · Concrete → pointing ·
                  Tin-tile → panel-beat). */}
              <details className="bg-white border border-slate-200 rounded-md" data-testid="repairs-addons-details">
                <summary className="cursor-pointer px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                  <Layers size={14} /> Stage-1 add-ons <span className="text-xs font-normal text-slate-500">(rescrew · pointing · panel-beat)</span>
                </summary>
                <div className="p-4 space-y-3" data-testid="repairs-addons-config">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    {showRescrew && (
                      <label className="block">
                        <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Iron rescrew m² @ ${addons.rescrew_per_m2}/m²</span>
                        <input type="number" data-testid="repairs-rescrew-input"
                          value={form.restoration_rescrew_m2}
                          onChange={(e) => setForm({ ...form, restoration_rescrew_m2: e.target.value, _rescrew_autofilled: false })}
                          className={`mt-1 w-full border rounded-md px-2 py-1.5 text-sm ${form._rescrew_autofilled ? 'border-amber-400 bg-amber-50' : 'border-slate-300'}`}
                          placeholder="0" />
                        {form._rescrew_autofilled && (
                          <p data-testid="repairs-rescrew-autofill-hint" className="mt-1 text-[10px] text-amber-700 leading-snug">
                            ✨ Auto-filled from quote type <strong>{appraisal?.quotation_type || appraisal?.quote_type_ref || ''}</strong> · roof m². Edit or hit <strong>Save add-ons</strong> to confirm.
                          </p>
                        )}
                        {/* S4 · measurement-driven auto-fill from REP-Rescrew-Iron rule */}
                        <AutoFillChip
                          option={bundle?.auto_qty_options?.['REP-Rescrew-Iron']}
                          onApply={(qty) => setForm({ ...form, restoration_rescrew_m2: qty, _rescrew_autofilled: false })}
                          currentValue={form.restoration_rescrew_m2}
                          testid="repairs-rescrew-autofill"
                        />
                      </label>
                    )}
                    {showPointing && (
                      <label className="block">
                        <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Ridge pointing lm @ ${addons.pointing_per_lm}/lm</span>
                        <input type="number" data-testid="repairs-pointing-input"
                          value={form.restoration_pointing_lm}
                          onChange={(e) => setForm({ ...form, restoration_pointing_lm: e.target.value })}
                          className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" placeholder="0" />
                        {/* S4 · measurement-driven auto-fill from REP-BedPoint-Ridge rule (Concrete + Clay) */}
                        <AutoFillChip
                          option={bundle?.auto_qty_options?.['REP-BedPoint-Ridge']}
                          onApply={(qty) => setForm({ ...form, restoration_pointing_lm: qty })}
                          currentValue={form.restoration_pointing_lm}
                          testid="repairs-pointing-autofill"
                        />
                      </label>
                    )}
                    {showPanelBeat && (
                      <label className="block">
                        <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Tin-tile panel beat (qty) @ ${addons.panel_beat_per_each}/ea</span>
                        <input type="number" data-testid="repairs-panel-beat-input"
                          value={form.restoration_panel_beat_qty}
                          onChange={(e) => setForm({ ...form, restoration_panel_beat_qty: e.target.value })}
                          className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" placeholder="0" />
                      </label>
                    )}
                    {!showRescrew && !showPointing && !showPanelBeat && (
                      <div className="text-xs text-slate-400 italic col-span-3">No Stage-1 add-ons defined for this subtype.</div>
                    )}
                  </div>
                  <div className="flex justify-end pt-2 border-t border-slate-100">
                    <button data-testid="repairs-save-addons-btn"
                      onClick={saveForm} disabled={saving}
                      className="bg-orange-600 text-white text-sm font-semibold rounded-md px-4 py-1.5 hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2">
                      {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                      {saving ? 'Saving…' : 'Save & recalculate'}
                    </button>
                  </div>

                  {/* Stage-1 lines table (repairs + add-ons combined) */}
                  <LinesTable
                    title="Stage 1 — Repair + add-on line items"
                    lines={[...(bundle.stage1_repair_lines || []), ...(bundle.stage1_addon_lines || [])]}
                    emptyMsg="No defect counts or add-on quantities yet. Add scenarios above or set add-on quantities to populate the line items."
                    testid="repairs-stage1-table"
                  />
                </div>
              </details>
            </>
          )}
        </>
      )}
    </div>
    </CalcShadowProvider>
  );
}

function SummaryCard({ label, value, tone, big = false, testid }) {
  const toneMap = {
    slate:   'bg-slate-50 border-slate-200 text-slate-800',
    sky:     'bg-sky-50 border-sky-200 text-sky-800',
    amber:   'bg-amber-50 border-amber-200 text-amber-800',
    orange:  'bg-orange-50 border-orange-300 text-orange-900',
  };
  return (
    <div data-testid={testid} className={`rounded-md border p-3 ${toneMap[tone] || toneMap.slate}`}>
      <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
      <div className={`mt-1 font-bold ${big ? 'text-2xl' : 'text-lg'}`}>{value}</div>
    </div>
  );
}

function LinesTable({ title, lines, emptyMsg, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={testid}>
      <div className="px-3 py-2 border-b border-slate-100 text-sm font-semibold text-slate-700">{title}</div>
      {(!lines || lines.length === 0) ? (
        <div className="p-4 text-xs text-slate-500 italic">{emptyMsg}</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-white text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left p-2 px-3">Type</th>
              <th className="text-left p-2 px-3">Description</th>
              <th className="text-right p-2 px-3 w-16">Qty</th>
              <th className="text-right p-2 px-3 w-16">Unit</th>
              <th className="text-right p-2 px-3 w-24">Rate</th>
              <th className="text-right p-2 px-3 w-28">Total</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((li, i) => (
              <tr key={`${li.area || i}-${li.type_name}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="p-2 px-3 font-semibold text-slate-800">{li.component_type}</td>
                <td className="p-2 px-3 text-slate-600 text-xs">{li.type_name}</td>
                <td className="p-2 px-3 text-right">{li.quantity}</td>
                <td className="p-2 px-3 text-right text-xs text-slate-500">{li.unit}</td>
                <td className="p-2 px-3 text-right">${(li.rate || 0).toFixed(2)}</td>
                <td className="p-2 px-3 text-right font-semibold">${(li.total || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
